import { defineStore, storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { api, type types } from "@/lib/openapi";
import { IntegrationType as IntegrationTypeEnum } from "@/lib/openapi/gen/types.gen";

export type IntegrationType = types.IntegrationType;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const IntegrationType = IntegrationTypeEnum;
export type Integration = types.Integration;

export const useIntegrationStore = (integration_type: IntegrationType) => {
    return defineStore(`${integration_type}-integration`, () => {
        const isLoading = ref(false);
        const error = ref<string>();
        const isSetup = ref(false);
        const integration = ref<Integration>();
        const router = useRouter();

        const createApiIntegration = async (apiKey: string, host?: string) => {
            isLoading.value = true;
            error.value = undefined;

            try {
                const { response, data } =
                    await api.createApiIntegrationStekIntegrationMgtIntegrationSettingsIntegrationTypePost(
                        {
                            path: { integration_type },
                            body: { api_key: apiKey, host },
                        }
                    );

                if (response.ok) {
                    isSetup.value = true;
                    integration.value = data as Integration;
                }
            } catch (err) {
                console.error("Error creating API integration:", err);
                error.value = "Failed to connect to the server";
            } finally {
                isLoading.value = false;
            }
        };

        const testApiIntegration = async (apiKey: string, host?: string) => {
            isLoading.value = true;
            error.value = undefined;
            try {
                // Use the backend API endpoint for testing connection
                const { response } =
                    await api.testIntegrationConnectionStekIntegrationMgtIntegrationTestIntegrationTypePost(
                        {
                            body: {
                                api_key: apiKey,
                                host: host,
                            },
                            path: {
                                integration_type: IntegrationType.GITLAB,
                            },
                        }
                    );

                if (response.ok) {
                    return true;
                } else {
                    error.value =
                        response.statusText || "Connection test failed";
                }
            } catch (err) {
                const errorMessage =
                    (err as Error & { detail?: string })["detail"] ||
                    "Unknown error";
                error.value =
                    errorMessage ||
                    "Failed to connect to GitLab. Please check your settings.";
            } finally {
                isLoading.value = false;
            }

            return false;
        };

        const connect = async () => {
            const redirectRoute = router.resolve({ name: "oauthCallback" });
            try {
                isLoading.value = true;
                error.value = "";

                const { response, data } =
                    await api.prepareIntegrationsOauth2ClientPrepareIntegrationTypeGet(
                        {
                            path: { integration_type },
                            query: {
                                redirect_uri: new URL(
                                    redirectRoute.fullPath,
                                    window.location.origin
                                ).toString(),
                            },
                        }
                    );

                if (!response.ok || !data) {
                    error.value = `Error initiating OAuth flow: ${response.statusText}`;
                    throw new Error(error.value);
                }

                // Redirect to ClickUp for authorization
                window.location.assign(data.auth_url);
            } catch (err) {
                console.error("Error initiating OAuth2 flow:", err);
                error.value =
                    "Failed to connect with ClickUp. Please try again.";
                isLoading.value = false;
            }
        };

        const fetchSettings = async (): Promise<undefined> => {
            isLoading.value = true;
            error.value = undefined;

            try {
                const { data, response } =
                    await api.getIntegrationSettingsStekIntegrationMgtIntegrationSettingsIntegrationTypeGet(
                        { path: { integration_type } }
                    );

                if (data && response.ok) {
                    isSetup.value = true;
                    integration.value = data;
                    return;
                } else {
                    // If not found or not configured yet
                    if (response.status === 404) {
                        isSetup.value = false;
                        integration.value = undefined;
                    }

                    const errorData = await response
                        .json()
                        .catch(() => ({ detail: "Unknown error" }));
                    error.value =
                        errorData.detail || "Failed to load ClickUp settings";
                    return undefined;
                }
            } catch (err) {
                console.error("Error fetching ClickUp settings:", err);
                error.value = "Failed to connect to the server";
                return undefined;
            } finally {
                isLoading.value = false;
            }
        };

        const deleteSettings = async (): Promise<boolean> => {
            isLoading.value = true;
            error.value = undefined;

            try {
                const { response } =
                    await api.deleteIntegrationSettingsStekIntegrationMgtIntegrationSettingsIntegrationTypeDelete(
                        { path: { integration_type } }
                    );

                if (response.ok) {
                    isSetup.value = false;
                    return true;
                } else {
                    const errorData = await response
                        .json()
                        .catch(() => ({ detail: "Unknown error" }));
                    error.value =
                        errorData.detail ||
                        "Failed to delete ClickUp integration";
                    return false;
                }
            } catch (err) {
                console.error("Error deleting ClickUp settings:", err);
                error.value = "Failed to connect to the server";
                return false;
            } finally {
                isLoading.value = false;
            }
        };

        onMounted(async () => {
            await fetchSettings();
        });

        return {
            isSetup,
            isLoading,
            error,
            integration,
            connect,
            createApiIntegration,
            deleteSettings,
            fetchSettings,
            testApiIntegration,
        };
    })();
};

export const useIntegrationManager = (
    ...args: Parameters<typeof useIntegrationStore>
) => storeToRefs(useIntegrationStore(...args));
